<template>
    <BaseCard theme="cream" radius="lgr" padding="none" class="w-full min-w-0 md:w-full max-w-[600px] overflow-hidden" data-testid="assessment-results-card">
        <template v-if="selectedReport">
            <div class="flex items-center gap-3 p-4">
                <span class="icon" aria-hidden="true"><i class="bi bi-file-text-fill"></i></span>
                <div class="flex min-w-0 flex-1 flex-col">
                    <h3 class="nadia-chat-bold truncate text-ink" :title="selectedReport.name">{{ selectedReport.name }}</h3>
                    <span class="scaffold-details-small text-ink-muted">{{ foundMeta }}</span>
                </div>
                <!-- Inertia intercepts target="_blank"; a native link keeps this conversation open. -->
                <a
                    :href="selectedReport.results_path"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="base-btn-neutral base-btn-sm shrink-0 no-underline"
                    :aria-label="t('chat.assessmentResults.openAriaLabel', { name: selectedReport.name })">
                    {{ t("chat.assessmentResults.open") }}
                </a>
            </div>
        </template>
        <template v-else-if="result?.status === 'available' && reports.length">
            <div class="flex items-center gap-3 p-4">
                <span class="icon" aria-hidden="true"><i class="bi bi-file-text-fill"></i></span>
                <h3 class="nadia-chat-bold text-ink">{{ t("chat.assessmentResults.title") }}</h3>
            </div>
            <ul ref="reportList" class="divide-y divide-border border-t border-border">
                <li v-for="assessment in visibleReports" :key="assessment.cycle_id" class="group flex items-center justify-between gap-3 p-4">
                    <div class="flex min-w-0 flex-col gap-1">
                        <h4 class="body-small-semibold break-words text-ink">{{ assessment.name }}</h4>
                        <span class="scaffold-details-small text-ink-muted">
                            <template v-if="assessment.generated_at">{{
                                t("chat.assessmentResults.meta", { kind: reportKind(assessment), date: formatDate(assessment.generated_at, "long") })
                            }}</template>
                            <template v-else>{{ reportKind(assessment) }}</template>
                        </span>
                    </div>
                    <!-- Inertia intercepts target="_blank"; a native link keeps this conversation open. -->
                    <a
                        :href="assessment.results_path"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="base-btn-neutral base-btn-sm shrink-0 no-underline"
                        :aria-label="t('chat.assessmentResults.exploreAriaLabel', { name: assessment.name })">
                        {{ t("chat.assessmentResults.explore") }}
                    </a>
                </li>
            </ul>
            <div v-if="reports.length > VISIBLE_REPORTS || result.has_more" class="flex flex-col items-start gap-2 border-t border-border bg-surface-sunken p-4">
                <BaseButton v-if="reports.length > VISIBLE_REPORTS" theme="neutral" size="sm" class="min-h-11" :aria-expanded="expanded" @click="toggleReports">
                    {{
                        expanded
                            ? t("chat.assessmentResults.showFewer")
                            : reports.length - VISIBLE_REPORTS === 1
                              ? t("chat.assessmentResults.showMoreOne", { count: reports.length - VISIBLE_REPORTS })
                              : t("chat.assessmentResults.showMoreMany", { count: reports.length - VISIBLE_REPORTS })
                    }}
                    <i :class="expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="ml-2" aria-hidden="true"></i>
                </BaseButton>
                <p v-if="result.has_more && (expanded || reports.length <= VISIBLE_REPORTS)" class="scaffold-body-small text-ink-muted">
                    {{ t("chat.assessmentResults.olderReportHint") }}
                </p>
            </div>
        </template>
        <div v-else class="flex items-center gap-3 p-4">
            <span class="icon" aria-hidden="true"><i class="bi bi-file-text-fill"></i></span>
            <div class="flex min-w-0 flex-col">
                <h3 class="nadia-chat-bold text-pretty text-ink">{{ emptyHeading }}</h3>
                <p class="scaffold-details-small text-pretty text-ink-muted">{{ emptyDescription }}</p>
            </div>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import { ACTION } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import type { ChatMessageLine, ParseActionsAssessmentResultsAction } from "~vue/types/chat";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({ inheritAttrs: false });
const { t } = useI18n();
const { action = null } = defineProps<{ action?: ChatMessageLine | null }>();
const VISIBLE_REPORTS = 3;
const expanded = ref(false);
const reportList = useTemplateRef("reportList");
const result = computed(() => (action?.type === "action" && action.action_name === ACTION.GET_ASSESSMENT_RESULTS ? action.action_params : null));
const reports = computed(() => result.value?.assessments ?? []);
const selectedReport = computed(() => (result.value?.status === "found" ? reports.value[0] : null));
const foundMeta = computed(() => {
    const report = selectedReport.value;
    if (!report) {
        return "";
    }
    if (!report.generated_at) {
        return reportKind(report);
    }
    return t("chat.assessmentResults.meta", { kind: reportKind(report), date: t("chat.assessmentResults.generated", { date: formatDate(report.generated_at, "long") }) });
});
const visibleReports = computed(() => (expanded.value ? reports.value : reports.value.slice(0, VISIBLE_REPORTS)));
const emptyHeading = computed(() => (result.value?.status === "disabled" ? t("chat.assessmentResults.emptyDisabledHeading") : t("chat.assessmentResults.emptyHeading")));
const emptyDescription = computed(() =>
    result.value?.status === "disabled" ? t("chat.assessmentResults.emptyDisabledDescription") : t("chat.assessmentResults.emptyDescription"),
);

async function toggleReports(): Promise<void> {
    expanded.value = !expanded.value;
    if (expanded.value) {
        await nextTick();
        reportList.value?.querySelectorAll("a")[VISIBLE_REPORTS]?.focus();
    }
}

function reportKind(report: ParseActionsAssessmentResultsAction["action_params"]["assessments"][number]): string {
    return report.kind === "360" ? t("chat.assessmentResults.kind360") : t("chat.assessmentResults.kindSelf");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Neutral file tile, same as the role-play recap card. */
    .icon {
        @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-ink-muted;
    }
}
</style>
